import { useEffect, useRef, useState } from 'react';
import { IdentificationTypes } from '@/data/libraries/IdentificationTypes';
import { clean, format } from 'rut.js';

import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import { Col, Form, Row } from 'react-bootstrap';

import { useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';

import IdTypesController from '@/controllers/IdTypesController';
import UserController from '@/controllers/UserController';
import validateClientDetailsUseCase from '@/domain/useCases/validateClientDetailsUseCase';

import { translate } from '@/domain/utils/translate';

import { IIdTypes } from '@/domain/interfaces/IIdTypes';
import { IClientDetailsValidation, IIdentification, IPhone, IRegistrationData, IUserStore } from '@/domain/interfaces/IUser';
import { IClientDetail } from '@/domain/interfaces/ICart';
import { IRegisterResponse } from '@/domain/interfaces/IRegisterResponse';

import capitalize from '@/domain/utils/capitalize';

import InputForm from '@/ui/components/forms/InputForm';
import PhoneInputMain from '@/ui/components/PhoneInputMain';
import PasswordInput from '@/ui/components/user/PasswordInput';

interface UserDataFormProps {
    /** Objeto con la información del formulario de cliente preestablecida */
    clientData: IRegistrationData;
    /** Función que actualiza la data del cliente hacia el componente padre */
    updateClientData: (clientData: IRegistrationData) => void;
    /** Flag identifica si el formulario es para el registro de usuario */
    isRegister?: boolean;
    /** Si es necesario validar el email como existente se envia la función para manejar el error */
    handleShowEmailAlertModal?: () => void;
    /** Flag identifica si se quiere o no capturar el ID del cliente */
    withOfficialId?: boolean;
    /** Marca si el formilario no se puede editar */
    disabledForm?: boolean;
}

/**
 * Formulario datos de cliente
 * @component
 */
const UserDataForm = ({
    clientData,
    updateClientData,
    isRegister = false,
    handleShowEmailAlertModal = () => { },
    withOfficialId = true,
    disabledForm = false,
}: UserDataFormProps): JSX.Element => {

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();
    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /** Controlador que obtiene los tipos de identificación */
    const { getIdTypes } = IdTypesController();

    const { validateUserRegister } = UserController();

    /**Trae la información del usuario logeado desde Redux */
    const logedUser = useAppSelector((state: RootState) => state.userState) as IUserStore;

    /** Referencia al input nombre del cliente */
    const firstNameRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

    /** Referencia al input apellido del cliente */
    const lastNameRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

    /** Referencia al select del tipo de documento */
    const documentTypeRef: React.MutableRefObject<HTMLSelectElement | null> = useRef<HTMLSelectElement | null>(null);

    /** Referencia al input de numero de documento */
    const identificationIdRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

    /** Referencia al input de correo electrónico */
    const emailRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

    /** Referencia al input para teléfono */
    const phoneRef = useRef<IPhone>({ country: '', number: '' });

    /** Referencia al input password */
    const passwordRef = useRef<HTMLInputElement | null>(null);

    /**Estado de error del input de nombre */
    const [passwordInputWithError, setPasswordInputWithError] = useState<boolean>(false);

    /** Estado inicial de arreglo de tipos */
    const [idTypes, setIdTypes] = useState<IIdTypes[]>([]);

    /**Estado de error del input de nombre */
    const [firstNameInputWithError, setFirstNameInputWithError] = useState<boolean>(false);

    /**Mensaje de error del input de nombre  */
    const [firstNameInputErrorMessage, setFirstNameInputErrorMessage] = useState<string>('');

    /**Estado de error del input de apellido */
    const [lastNameInputWithError, setLastNameInputWithError] = useState<boolean>(false);

    /**Mensaje de error del input de apellido  */
    const [lastNameInputErrorMessage, setLastNameInputErrorMessage] = useState<string>('');

    /** Obtiene la identificación inicial desde localStorage */
    const getInitialIdentification = (): IIdentification => {
        /** Intentar recuperar el tipo de documento guardado en localStorage */
        const savedIdentification = localStorage.getItem('checkout_identification');
        if (savedIdentification) {
            try {
                return JSON.parse(savedIdentification);
            } catch (error) {
                /** Si hay error al parsear, limpiar el localStorage para evitar errores futuros */
                localStorage.removeItem('checkout_identification');
                return { type: '', value: '' };
            }
        }
        return { type: '', value: '' };
    };

    /**Estado de valores del input para identificación */
    const [identificationId, setIdentificationId] = useState<IIdentification>(getInitialIdentification);

    /**Estado de error del input de documento */
    const [documentInputWithError, setDocumentInputWithError] = useState<boolean>(false);

    /**Mensaje de error del input de documento  */
    const [documentInputErrorMessage, setDocumentInputErrorMessage] = useState<string>('');

    /**Estado de error del input de email */
    const [emailInputWithError, setEmailInputWithError] = useState<boolean>(false);

    /**Mensaje de error del input de email  */
    const [emailInputErrorMessage, setEmailInputErrorMessage] = useState<string>('');

    /**Estado de error del input de teléfono */
    const [phoneInputWithError, setPhoneInputWithError] = useState<boolean>(false);
    /**Mensaje de error del input de teléfono  */
    const [phoneInputErrorMessage, setPhoneInputErrorMessage] = useState<string>('');

    /** Función para guardar el tipo de documento en localStorage */
    const saveIdentificationToLocalStorage = (identification: IIdentification) => {
        localStorage.setItem('checkout_identification', JSON.stringify(identification));
    };

    /** Trae los tipos de identificación del back */
    const fetchIdTypes = async (): Promise<void> => {
        /** Uso del controlador useIdTypesController con la función getIdTypes */
        const getIdTypesDropdown: IIdTypes[] = await getIdTypes();
        setIdTypes(getIdTypesDropdown);
    };

    const onkeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    /**Validación de los campos del formulario de cliente y actualización de los valores en el DOM y en el carrito de compras*/
    const handleValidate = async (): Promise<void> => {
        const firstName = capitalize(firstNameRef.current?.value ?? '');
        const lastName = capitalize(lastNameRef.current?.value ?? '');
        const identificationIdType = documentTypeRef.current?.selectedOptions[0]?.value ?? identificationId.type;
        const identificationIdValue = identificationIdRef.current?.value ?? identificationId.value;
        const email = (emailRef.current?.value ?? '').toLowerCase();

        /** Actualizar los valores de los campos de nombre y apellido en el DOM */
        if (firstNameRef.current) {
            firstNameRef.current.value = firstName;
        }
        if (lastNameRef.current) {
            lastNameRef.current.value = lastName;
        }

        /** Asegurarse de que el tipo de documento sea consistente entre el DOM y el estado */
        if (documentTypeRef.current && identificationIdType && identificationIdType !== documentTypeRef.current.value) {
            documentTypeRef.current.value = identificationIdType;
        }

        /** Guardar identificación en localStorage */
        saveIdentificationToLocalStorage({
            type: identificationIdType,
            value: identificationIdValue
        });

        /** Establece el formulario a actualizar */
        const clientDelails: IClientDetail = {
            firstName: firstName,
            lastName: lastName,
            identificationId: {
                type: identificationIdType,
                value: identificationIdValue,
            },
            email: email,
            phone: phoneRef.current ?? { country: '', number: '' },
            shippingType: null,
            password: passwordRef.current?.value ?? '',
            error: true,
        }

        /** Valida desde el controlador */
        const errors: IClientDetailsValidation[] = validateClientDetailsUseCase(clientDelails);

        const KEY_FIRST_NAME = 'FIRST_NAME';
        const KEY_LAST_NAME = 'LAST_NAME';
        const KEY_DOCUMENT = 'DOCUMENT_ID';
        const KEY_EMAIL = 'EMAIL';
        const KEY_PHONE = 'PHONE';
        const KEY_PASSWORD = 'PASSWORD';

        /** Validar errores en los campos del formulario de cliente y actualizar los mensajes de error */
        const firstNameError = errors.find((element) => element.field === KEY_FIRST_NAME);
        /**si hay error en el campo de nombre se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */
        if (firstNameError) {
            setFirstNameInputWithError(true);
            setFirstNameInputErrorMessage(firstNameError.message);
        } else {
            setFirstNameInputWithError(false);
            setFirstNameInputErrorMessage('');
        }
        /**si hay error en el campo de apellido se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */
        const lastNameError = errors.find((element) => element.field === KEY_LAST_NAME);

        if (lastNameError) {
            setLastNameInputWithError(true);
            setLastNameInputErrorMessage(lastNameError.message);
        } else {
            setLastNameInputWithError(false);
            setLastNameInputErrorMessage('');
        }

        const documentError = errors.find((element) => element.field === KEY_DOCUMENT);
        /**si hay error en el campo de documento se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */
        if (documentError) {
            setDocumentInputWithError(true);
            setDocumentInputErrorMessage(documentError.message);
        } else {
            setDocumentInputWithError(false);
            setDocumentInputErrorMessage('');
        }

        const phoneError = errors.find((element) => element.field === KEY_PHONE);
        /**si hay error en el campo de teléfono se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */
        if (phoneError) {
            setPhoneInputWithError(true);
            setPhoneInputErrorMessage(phoneError.message);
        } else {
            setPhoneInputWithError(false);
            setPhoneInputErrorMessage('');
        }

        let emailError = errors.find((element) => element.field === KEY_EMAIL);
        /**si hay error en el campo de email se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */

        let registerError: boolean = false;

        if (isRegister && !emailError) {

            /** Valida desde el controlador */
            const registrationResponse: IRegisterResponse = await validateUserRegister(clientDelails);
            if (registrationResponse) {
                emailError = registrationResponse.errors.find((element) => element.field === KEY_EMAIL);
                if (emailError) {
                    registerError = true;
                    emailRef.current!.value = '';
                    handleShowEmailAlertModal && handleShowEmailAlertModal();
                }
            }

            const passwordError = registrationResponse.errors.find((element) => element.field === KEY_PASSWORD);
            /**si hay error en el campo de constraseña se muestra el mensaje de error y se pone el estado de error en true sino se pone en false y se limpia el mensaje de error */
            if (passwordRef.current?.value === '' || passwordError) {
                registerError = true;
                setPasswordInputWithError(true);
            } else {
                setPasswordInputWithError(false);
            }
        }

        if (emailError) {
            setEmailInputWithError(true);
            setEmailInputErrorMessage(emailError.message);
        } else {
            setEmailInputWithError(false);
            setEmailInputErrorMessage('');
        }

        /** Actualizar el formulario de carrito de compras con los valores validados */
        if (errors.length === 0 && !registerError) {
            clientDelails.error = false;
            /**se formatea el rut antes de mandarlo a actualizar */
            if (clientDelails.identificationId.type === IdentificationTypes.RUT_TYPE.toString()) {
                clientDelails.identificationId.value = format(identificationIdValue, { dots: false });
            }
        } else {
            // Si withOfficialId es false, solo consideramos errores relevantes (ignoramos documento)
            if (!withOfficialId) {
                // Filtrar los errores de documento ya que no son necesarios para este formulario
                const relevantErrors = errors.filter(err => err.field !== KEY_DOCUMENT);
                clientDelails.error = relevantErrors.length > 0 || registerError;
            } else {
                clientDelails.error = true;
            }
        }

        // Actualiza los datos del cliente en el componente padre
        updateClientData(clientDelails);
    };

    /** Precargar data de cliente */
    const loadClientData = (): void => {
        if (clientData) {
            /** Configura data del liente */
            let firstName: string = '';
            if (clientData.firstName && clientData.firstName.trim().length > 0) {
                firstName = clientData.firstName;
            } else {
                firstName = logedUser.user.firstName ?? '';
            }

            let lastName: string = '';
            if (clientData.lastName && clientData.lastName.trim().length > 0) {
                lastName = clientData.lastName;
            } else {
                lastName = logedUser.user.lastName ?? '';
            }

            let email: string = '';
            if (clientData.email && clientData.email.trim().length > 0) {
                email = clientData.email;
            } else {
                email = logedUser.user.email ?? '';
            }

            const phone: IPhone = {
                number: '',
                country: '',
            };
            if (clientData.phone.number && clientData.phone.number.trim().length > 0) {
                phone.number = clientData.phone.number;
                phone.country = clientData.phone.country;
            } else {
                if (logedUser.user.userPhone?.number && logedUser.user.userPhone?.country) {
                    phone.number = logedUser.user.userPhone.number;
                    phone.country = logedUser.user.userPhone.country;
                }
            }

            let identificationIdType: string = '0';
            if (clientData.identificationId.type && clientData.identificationId.type.trim().length > 0 && clientData.identificationId.type !== '0') {
                identificationIdType = clientData.identificationId.type.toString() ?? '0';
            } else {
                identificationIdType = logedUser.user.idTypeIdUser?.toString() ?? '0';
            }

            let identificationIdValue: string = '';
            if (clientData.identificationId.value && clientData.identificationId.value.trim().length > 0) {
                identificationIdValue = identificationIdType === IdentificationTypes.RUT_TYPE.toString()
                    ? clean(clientData.identificationId.value ?? '')
                    : clientData.identificationId.value ?? '';
            } else {
                identificationIdValue = identificationIdType === IdentificationTypes.RUT_TYPE.toString()
                    ? clean(logedUser.user.officialIdUser ?? '')
                    : logedUser.user.officialIdUser ?? '';
            }

            /** Actualizar el estado de identificationId primero */
            setIdentificationId({
                type: identificationIdType,
                value: identificationIdValue
            });

            /** Pre-establece data del cliente */
            firstNameRef.current!.value = firstName;
            lastNameRef.current!.value = lastName;
            emailRef.current!.value = email;
            phoneRef.current = phone;

            if (documentTypeRef.current && identificationIdType.length > 0 && identificationIdType != '0') {
                documentTypeRef.current!.value = identificationIdType;
            }
            if (identificationIdRef.current) {
                identificationIdRef.current!.value = identificationIdValue;
            }

            handleValidate();
        }
    }

    /** UseEffect precargar data de cliente */
    useEffect((): void => {
        loadClientData();
        window.scrollTo(0, 0);
    }, [idTypes]);

    useEffect((): void => {
        /** Trae los tipos de identificación */
        fetchIdTypes();
    }, []);

    /** Mantener sincronizado el estado identificationId con clientData */
    useEffect(() => {
        if (clientData && clientData.identificationId) {
            setIdentificationId({
                type: clientData.identificationId.type,
                value: clientData.identificationId.value
            });
        }
    }, [clientData]);

    /**CONSTANTES DE TEXTO */
    const CLIENT_INFO_NAME_LABEL = translate('checkout.client_info_name_label');
    const CLIENT_INFO_LAST_NAME_LABEL = translate('checkout.client_info_last_name_label');
    const CLIENT_INFO_DOCUMENT_TYPE_LABEL = translate('checkout.client_info_document_type_label');
    const CLIENT_INFO_DOCUMENT_ID_LABEL = translate('checkout.client_info_document_id_label');
    const CLIENT_INFO_EMAIL_LABEL = translate('checkout.client_info_email_label');
    const CLIENT_INFO_PHONE_NUMBER_LABEL = translate('checkout.client_info_phone_number_label');

    return (

        <Row className='g-0'>
            <Col>
                <Form
                    onKeyDown={onkeyDown}
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                    className='svg-tertiary-1'
                >
                    <Row className='my-2 g-0'>
                        <Col lg={12} xl={6}>
                            <InputForm
                                iconName='ico-error'
                                disabled={disabledForm}
                                className='my-2 px-1'
                                classLabel={isMobile ? 'p-regular text-secondary-1 ' : 'p-smaller text-secondary-1'}
                                label={CLIENT_INFO_NAME_LABEL}
                                inputRef={firstNameRef}
                                onChange={() => handleValidate()}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                withErrors={firstNameInputWithError}
                                messageError={firstNameInputErrorMessage}
                            />
                        </Col>
                        <Col lg={12} xl={6}>
                            <InputForm
                                iconName='ico-error'
                                className='my-2 px-1'
                                disabled={disabledForm}
                                classLabel={isMobile ? 'p-regular text-secondary-1 ' : 'p-smaller text-secondary-1'}
                                label={CLIENT_INFO_LAST_NAME_LABEL}
                                inputRef={lastNameRef}
                                onChange={() => handleValidate()}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                withErrors={lastNameInputWithError}
                                messageError={lastNameInputErrorMessage}
                            />
                        </Col>
                    </Row>
                    {withOfficialId && (
                        <Row className='my-2 g-0'>
                            <Col lg={12} xl={6}>
                                <Form.Group className='pt-2 px-1'>
                                    <div className='d-flex justify-content-between'>
                                        <Form.Label>
                                            <span className={`text-nowrap p-regular-medium`}>{CLIENT_INFO_DOCUMENT_TYPE_LABEL}</span>
                                        </Form.Label>
                                    </div>
                                    <Form.Select
                                        aria-label='CLIENT_INFO_DOCUMENT_TYPE_LABEL'
                                        ref={documentTypeRef}
                                        disabled={disabledForm}
                                        onChange={(value) => {
                                            const newType = value.target.value;
                                            const newValue = newType === '' ? '' : (identificationIdRef.current?.value ?? '');

                                            /** Actualizar el estado local primero */
                                            const newIdentification = {
                                                type: newType,
                                                value: newValue,
                                            };

                                            setIdentificationId(newIdentification);

                                            /** Guardar en localStorage */
                                            saveIdentificationToLocalStorage(newIdentification);

                                            /** Limpiar el campo de valor si el tipo está vacío */
                                            if (newType === '' && identificationIdRef.current) {
                                                identificationIdRef.current.value = '';
                                            }

                                            /** Validar inmediatamente para actualizar el estado global */
                                            setTimeout(() => handleValidate(), 0);
                                        }}
                                        className={`form-control ${documentInputWithError ? 'form-control-error' : ''}  position-relative`}>
                                        <option value={''}>{CLIENT_INFO_DOCUMENT_TYPE_LABEL}</option>
                                        {idTypes?.map((element) => {
                                            return (
                                                <option key={element.id} value={element.id}>
                                                    {element.title}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={12} xl={6}>
                                <InputForm
                                    iconName='ico-error'
                                    className='my-2 px-1 '
                                    label={CLIENT_INFO_DOCUMENT_ID_LABEL}
                                    inputRef={identificationIdRef}
                                    disabled={(documentTypeRef.current?.value === '' && identificationIdRef.current?.value === '') || disabledForm}
                                    onChange={(value) => {
                                        const newIdentification = {
                                            type: documentTypeRef.current?.value ?? '',
                                            value: value.target.value,
                                        };

                                        setIdentificationId(newIdentification);

                                        /** Guardar en localStorage */
                                        saveIdentificationToLocalStorage(newIdentification);

                                        handleValidate();
                                    }}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        return false;
                                    }}
                                    withErrors={documentInputWithError}
                                    messageError={documentInputErrorMessage}
                                />
                            </Col>
                        </Row>
                    )}
                    <Row className='my-2 g-0'>
                        <Col lg={12} xl={6}>
                            <InputForm
                                iconName='ico-error'
                                className='my-2 px-1'
                                disabled={disabledForm}
                                label={CLIENT_INFO_EMAIL_LABEL}
                                inputRef={emailRef}
                                onChange={() => handleValidate()}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                withErrors={emailInputWithError}
                                messageError={emailInputErrorMessage}
                            />
                        </Col>
                        <Col lg={12} xl={6}>
                            <PhoneInputMain
                                phone={phoneRef}
                                classWithError={phoneInputWithError}
                                phoneNumberLabel={CLIENT_INFO_PHONE_NUMBER_LABEL}
                                phoneErrorMessage={phoneInputErrorMessage}
                                validateForm={handleValidate}
                                disabledField={disabledForm}
                            />
                        </Col>
                    </Row>
                    {isRegister && (
                        <PasswordInput
                            passwordRef={passwordRef}
                            email={emailRef.current?.value ?? ''}
                            passwordInputWithError={passwordInputWithError}
                            handleValidate={handleValidate}
                        />
                    )}
                </Form>
            </Col>
        </Row>

    )
}

export default UserDataForm;